import React from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'

import { selectFullscreen } from '@store/actionSlices/appConfig'
import { setExternalViews } from '@store/actionSlices/externalViews'
import {
  ConfigInterface,
  setAspect,
  setConfigLoaded,
  setDisableSoldPropertyInfo,
  setEnvisionVRConfiguration,
  setGallerySettings,
  setHouseAndLandConfig,
  setLightUpBuildingModelOnFilter,
  setMarkerColour,
  setNavigationSettings,
  setPrices,
  setProjectConfig,
  setProjectType,
  setPropertyConfig,
  setSnaploaderConfiguration,
  setStatusColors,
  setStatusLabels,
  setTemplateConfigs,
  setThemeConfiguration,
  setThreeDViewerUrl,
  setUnitTypes,
  setVideoScreensaver,
} from '@store/actionSlices/projectConfig'
import { ShortlistState } from '@store/actionSlices/shortlist'
import { setWhiteLabel } from '@store/actionSlices/whiteLabel'
import { NavigationMenuKey, RouteKey } from '@store/constants'
import { MenuItemInterface, RootStateTypeExtra, SessionMap } from '@store/types'

import {
  selectFromResult as selectFromResultConfig,
  useGetConfigQuery,
} from '@api/config'
import {
  ExternalViewsData,
  selectFromResult as selectFromExternalViewsResult,
  useGetExternalViewsQuery,
} from '@api/external-views'
import {
  WhiteLabel,
  selectFromWhiteLabelResult,
  useGetWhiteLabelsQuery,
} from '@api/white-label'

import { ProjectType } from '@utilities/adgroup-utilities/constants/common'
import { hasEnvisionVR, hasSnaploader } from '@utilities/helper'
import useGetShowcaseRoomsConfig from '@utilities/use-get-showcase-rooms-config'
import useHiddenNavs from '@utilities/use-hidden-navs'

import { StarSvg } from '@svg/react'

import AssetHandler from '@adUtilities/asset-handler/asset-handler'
import useImageURLValidator from '@adUtilities/hooks/image-url-validator'

import { FULFILLED_STATUS } from '../data-handler/data-handler'
import ExternalMenuItems from './external-menu-items'
import MENU_ITEMS from './menu-items'

const DEFAULT_LOGO_URL = `${process.env.REACT_APP_BUCKET}/showcase_logo_dark_1280px.svg`

interface ComponentProps {
  projectName: string
  room: string
  projectConfig: ConfigInterface
  externalViews: Array<ExternalViewsData>
  shortlists: ShortlistState['shortlists']
  session: SessionMap | undefined
  whiteLabel: WhiteLabel
}

function Navigation({
  projectName,
  room,
  projectConfig,
  externalViews,
  shortlists,
  session,
  whiteLabel,
}: ComponentProps) {
  const dispatch = useDispatch()
  const location = useLocation()

  const hiddenNavs = useHiddenNavs()

  const {
    isLoaded: isRoomConfigLoaded,
    navigationSettings,
    theme,
    templates,
    videoScreensaver,
  } = useGetShowcaseRoomsConfig({ projectName, room: room || 'default' })

  const [activeMenu, setActiveMenu] = React.useState('')
  const [externalMenuItemsToggle, setExternalMenuItemsToggle] =
    React.useState(false)
  const [skipExternalViewsApi, setSkipExternalViewsApi] = React.useState(true)

  const fullscreen = useSelector(selectFullscreen)

  const fullScreenMode = React.useMemo(() => {
    if (session?.connected) {
      return true
    }
    return fullscreen
  }, [session?.connected, fullscreen])

  const {
    prices,
    configInfo,
    threeDViewer,
    markerColour,
    snaploaderConfiguration,
    envisionVRConfiguration,
    propertyConfig,
    houseAndLandConfig,
    aspects,
    unitTypes,
    status,
    gallerySettings,
    disableSoldProperty,
    type: projectType,
    lightUpBuildingModelOnFilter,
    statusLabels,
    statusColors,
  } = useGetConfigQuery(
    { projectName },
    { selectFromResult: selectFromResultConfig }
  )

  const whiteLabelPayload = useGetWhiteLabelsQuery(
    { projectName },
    { selectFromResult: selectFromWhiteLabelResult }
  )

  const externalViewsPayload = useGetExternalViewsQuery(
    { projectName },
    {
      selectFromResult: selectFromExternalViewsResult,
      skip:
        navigationSettings.find(
          (item: MenuItemInterface) =>
            item.key === NavigationMenuKey.EXTERNAL_ROUTES && item.active
        ) === undefined,
    }
  )

  const navigationItems = React.useMemo(() => {
    if (!projectConfig?.configLoaded) {
      return []
    }

    if (navigationSettings.length < 1) {
      return MENU_ITEMS
    }

    return MENU_ITEMS.map((item: MenuItemInterface) => {
      const foundItem = navigationSettings.find(
        (navigationSetting: MenuItemInterface) =>
          navigationSetting.key === item.key
      )

      return foundItem
        ? {
            ...item,
            label: foundItem.label,
            active: foundItem.active,
            order: foundItem.order,
          }
        : item
    })
      .sort((a, b) => {
        if (!Number.isNaN(a.order ?? NaN) && !Number.isNaN(b.order ?? NaN)) {
          return Number(a.order) - Number(b.order)
        }
        return MENU_ITEMS.indexOf(a) - MENU_ITEMS.indexOf(b)
      })
      .filter((item: MenuItemInterface) => !!item.active)
  }, [projectConfig?.configLoaded, navigationSettings])

  const shortlistMenuItem = React.useMemo(
    () =>
      navigationItems.find(
        (item: MenuItemInterface) => item.key === NavigationMenuKey.SHORTLIST
      ),
    [navigationItems]
  )

  const handleRouteForThreeDViews = () => {
    const { threeDViewerUrl } = projectConfig

    if (projectConfig.showcase3DIntegration) {
      return RouteKey.ADTHREED
    }

    if (hasSnaploader(projectConfig.snaploaderConfiguration)) {
      return RouteKey.SNAPLOADER_VIEW
    }

    if (hasEnvisionVR(projectConfig.envisionVRConfiguration)) {
      return RouteKey.THREE_D_BUILDING
    }

    if (threeDViewerUrl !== '' && threeDViewer?.active) {
      return RouteKey.AREA_VIEW_3D
    }

    return undefined
  }

  const getAreaViewRoute = () => {
    const { type } = projectConfig

    if (type === ProjectType.HouseAndLand) {
      return RouteKey.AREA_VIEW_HOUSE_AND_LAND
    }

    return RouteKey.AREA_VIEW
  }

  const defaultLogoUrl = React.useMemo(
    () =>
      AssetHandler({
        type: 'new',
        url: DEFAULT_LOGO_URL,
      }),
    []
  )

  const logoUrl = React.useMemo(
    () =>
      projectConfig?.logo?.visible && projectConfig?.logo?.url
        ? AssetHandler({
            type: 'new',
            url: projectConfig.logo.url,
          })
        : undefined,
    [projectConfig?.logo]
  )

  const { validatedUrl: logoSrc, isLoading: isLogoLoading } =
    useImageURLValidator(logoUrl ?? defaultLogoUrl, defaultLogoUrl)

  const getRouteLink = (argNavItem: MenuItemInterface): string => {
    if (argNavItem.key !== NavigationMenuKey.RESIDENCES) {
      return argNavItem.route || ''
    }

    const show3dViews = handleRouteForThreeDViews()

    if (show3dViews) {
      return show3dViews
    }

    return getAreaViewRoute()
  }

  React.useEffect(() => {
    const { externalViewsData, isLoaded: isExternalViewLoaded } =
      externalViewsPayload
    if (
      isExternalViewLoaded &&
      externalViewsData.length > 0 &&
      externalViews.length === 0
    ) {
      dispatch(setExternalViews(externalViewsData))
    }
  }, [externalViewsPayload])

  React.useEffect(() => {
    if (status === FULFILLED_STATUS && isRoomConfigLoaded) {
      const { min, max } = prices
      if (min && min.length >= 0 && max && max.length >= 0) {
        dispatch(setPrices(prices))
      }

      if (markerColour && Object.values(markerColour).length > 0) {
        dispatch(setMarkerColour(markerColour))
      }

      if (threeDViewer?.active && threeDViewer.url) {
        dispatch(setThreeDViewerUrl(threeDViewer.url))
      }

      if (Object.keys(configInfo).length > 0) {
        dispatch(setProjectConfig({ ...configInfo }))
      }

      if (
        snaploaderConfiguration &&
        Object.values(snaploaderConfiguration).length > 0
      ) {
        dispatch(setSnaploaderConfiguration(snaploaderConfiguration))
      }

      if (
        envisionVRConfiguration &&
        Object.values(envisionVRConfiguration).length > 0
      ) {
        dispatch(setEnvisionVRConfiguration(envisionVRConfiguration))
      }

      if (propertyConfig && Object.keys(propertyConfig).length > 0) {
        dispatch(setPropertyConfig(propertyConfig))
      }

      if (houseAndLandConfig && Object.keys(houseAndLandConfig).length > 0) {
        dispatch(setHouseAndLandConfig(houseAndLandConfig))
      }

      if (aspects && aspects.length >= 0) {
        dispatch(setAspect(aspects))
      }

      if (navigationSettings && navigationSettings.length >= 0) {
        dispatch(setNavigationSettings(navigationSettings))
      }

      if (templates && Object.keys(templates).length > 0) {
        dispatch(setTemplateConfigs(templates))
      }

      if (unitTypes && unitTypes.length >= 0) {
        dispatch(setUnitTypes(unitTypes))
      }

      if (theme && Object.values(theme).length > 0) {
        dispatch(setThemeConfiguration(theme))
      }

      if (videoScreensaver && Object.values(videoScreensaver).length > 0) {
        dispatch(setVideoScreensaver(videoScreensaver))
      }

      if (statusLabels && Object.values(statusLabels).length > 0) {
        dispatch(setStatusLabels(statusLabels))
      }

      if (statusColors && Object.values(statusColors).length > 0) {
        dispatch(setStatusColors(statusColors))
      }

      if (gallerySettings && Object.values(gallerySettings).length > 0) {
        dispatch(setGallerySettings(gallerySettings))
      }

      if (projectType) {
        dispatch(setProjectType(projectType))
      }

      dispatch(setDisableSoldPropertyInfo(disableSoldProperty))
      dispatch(setLightUpBuildingModelOnFilter(lightUpBuildingModelOnFilter))
      dispatch(setConfigLoaded(true))

      if (
        externalViews.length === 0 &&
        skipExternalViewsApi &&
        navigationSettings.find(
          (item) => item.key === NavigationMenuKey.EXTERNAL_ROUTES
        )
      ) {
        setSkipExternalViewsApi(false)
      }
    }
  }, [status, isRoomConfigLoaded])

  React.useEffect(() => {
    const { whiteLabelData, isLoaded } = whiteLabelPayload
    if (
      Object.values(whiteLabel).length === 0 &&
      isLoaded &&
      Object.values(whiteLabelData).length > 0
    ) {
      dispatch(setWhiteLabel(whiteLabelData))
    }
  }, [whiteLabelPayload])

  React.useEffect(() => {
    const paths = location.pathname.split('/')
    setActiveMenu(paths[2] || '')
  }, [location])

  return (
    <nav
      className={`absolute flex h-9 w-full items-center justify-between bg-neutralColour px-8 text-xs font-semibold tracking-widest transition-all duration-300 ${
        fullScreenMode ? '-top-9' : 'top-0 z-20'
      }`}
    >
      <div>
        {projectConfig?.configLoaded && (
          <Link
            title="Vision"
            to={{
              pathname: `/${projectName}/vision`,
              state: { from: location.pathname },
            }}
          >
            <img
              className={`h-7 ${isLogoLoading ? 'hidden' : ''}`}
              alt="logo"
              src={logoSrc}
            />
          </Link>
        )}
      </div>

      <ul className="flex h-full cursor-pointer items-center bg-neutralColour uppercase">
        {navigationItems
          .filter(
            (item: MenuItemInterface) =>
              item.key !== 'shortlist' && !hiddenNavs.includes(item.key)
          )
          .map((navItem: MenuItemInterface) =>
            navItem.key === 'external-routes' ? (
              <li className="relative h-full shrink-0" key={navItem.key}>
                <button
                  title={navItem.label}
                  type="button"
                  className={`nav-menu-item flex h-full items-center uppercase ${
                    navItem.relatedRoutes?.includes(activeMenu)
                      ? 'bg-secondaryColour'
                      : ''
                  }`}
                  onMouseEnter={() => setExternalMenuItemsToggle(true)}
                  onMouseLeave={() => setExternalMenuItemsToggle(false)}
                >
                  {navItem.label}
                </button>
                <ExternalMenuItems
                  externalViews={externalViews}
                  externalMenuItemsToggle={externalMenuItemsToggle}
                  setExternalMenuItemsToggle={setExternalMenuItemsToggle}
                />
              </li>
            ) : (
              <li className="h-full shrink-0" key={navItem.key}>
                <Link
                  title={navItem.label}
                  className={`nav-menu-item flex h-full items-center ${
                    navItem.relatedRoutes?.includes(activeMenu)
                      ? 'bg-secondaryColour'
                      : ''
                  }`}
                  to={{
                    pathname: `/${projectName}/${getRouteLink(navItem)}`,
                    state: { from: location.pathname },
                  }}
                >
                  {navItem.label}
                </Link>
              </li>
            )
          )}
      </ul>

      {shortlistMenuItem && !hiddenNavs.includes('shortlist') ? (
        <div className="flex items-center">
          <Link
            title={shortlistMenuItem.label}
            className="inline-flex items-center"
            to={{
              pathname: shortlistMenuItem.route,
              state: { from: location.pathname },
            }}
          >
            <div className="mr-2 rounded-full bg-secondaryColour p-1">
              <StarSvg
                className={`h-2.5 w-2.5 stroke-mainColour ${
                  shortlists.length > 0 ? 'fill-mainColour' : 'fill-transparent'
                }`}
              />
            </div>
            <span className="font-semibold uppercase text-mainColour">
              {shortlists.length > 0
                ? `${shortlists.length} SHORTLISTED`
                : shortlistMenuItem.label}
            </span>
          </Link>
        </div>
      ) : (
        <div></div>
      )}
    </nav>
  )
}

export default connect(
  ({
    projectIdentity: { projectName },
    projectConfig,
    externalViews,
    shortlist: { shortlists },
    firestore: { session },
    whiteLabel,
    appConfig: { room },
  }: RootStateTypeExtra) => ({
    session,
    projectName,
    projectConfig,
    externalViews,
    shortlists,
    whiteLabel,
    room,
  })
)(Navigation)
